import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, unwrap, type Authenticated } from '@/api/client';
import { legacyDeBeta, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/AuthProvider';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';

import { AuthLayout, AvisoRecaptcha, Campo } from './AuthLayout';
import { AvisoNoInvitado } from './AvisoNoInvitado';

type Challenge = components['schemas']['ChallengeRequired'];

const schema = z
  .object({
    name: z.string().trim().min(3, 'Escribe tu nombre completo').max(50, 'Máximo 50 caracteres'),
    email: z.string().trim().email('Escribe un correo válido').max(60),
    password: z.string().min(8, 'Mínimo 8 caracteres').max(128),
    confirmacion: z.string(),
    terminos: z.literal(true, { error: 'Acepta los términos y el aviso de privacidad' }),
  })
  .refine((v) => v.password === v.confirmacion, {
    path: ['confirmacion'],
    message: 'Las contraseñas no coinciden',
  });
type Form = z.input<typeof schema>;

export function RegistroPage() {
  const [noInvitado, setNoInvitado] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (form: Form) => {
    try {
      const token = await recaptchaToken('register');
      const result = await unwrap<Authenticated | Challenge>(
        api.POST('/api/v1/auth/register', {
          body: { name: form.name, email: form.email, password: form.password },
          headers: recaptchaHeader(token),
        }),
      );
      if (result.status === 'authenticated') {
        signIn(result);
        toast.success('¡Te damos la bienvenida a Consalud ENARM!');
        void navigate('/inicio', { replace: true });
      } else {
        void navigate('/registro/verificar', {
          state: { challenge: result, proposito: 'register' },
        });
      }
    } catch (err) {
      const beta = legacyDeBeta(err);
      if (beta) setNoInvitado(beta);
      else toast.error(mensajeDe(err));
    }
  };

  return (
    <AuthLayout titulo="Crea tu cuenta">
      {noInvitado && <AvisoNoInvitado url={noInvitado} />}
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <Campo etiqueta="Nombre completo" error={errors.name?.message}>
          <input className="input-vidrio" autoComplete="name" {...register('name')} />
        </Campo>
        <Campo etiqueta="Correo electrónico" error={errors.email?.message}>
          <input
            type="email"
            className="input-vidrio"
            autoComplete="email"
            {...register('email')}
          />
        </Campo>
        <Campo etiqueta="Contraseña" error={errors.password?.message}>
          <input
            type="password"
            className="input-vidrio"
            autoComplete="new-password"
            {...register('password')}
          />
        </Campo>
        <Campo etiqueta="Confirma tu contraseña" error={errors.confirmacion?.message}>
          <input
            type="password"
            className="input-vidrio"
            autoComplete="new-password"
            {...register('confirmacion')}
          />
        </Campo>
        <label className="flex items-start gap-2 text-sm text-texto-2">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-[#005DB9]"
            {...register('terminos')}
          />
          <span>
            Acepto los{' '}
            <a href="/terminos-y-condiciones/" target="_blank" rel="noreferrer">
              Términos y condiciones
            </a>{' '}
            y el{' '}
            <a href="/aviso-de-privacidad/" target="_blank" rel="noreferrer">
              Aviso de privacidad
            </a>
            .
          </span>
        </label>
        {errors.terminos && (
          <p role="alert" className="text-xs font-semibold text-rojo-texto">
            {errors.terminos.message}
          </p>
        )}
        <button
          type="submit"
          className="btn-cs btn-exito w-full py-3 text-base"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creando tu cuenta…' : 'Crear cuenta'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-texto-2">
        ¿Ya tienes cuenta?{' '}
        <Link to="/login" className="font-bold">
          Inicia sesión
        </Link>
      </p>
      <AvisoRecaptcha />
    </AuthLayout>
  );
}
